import { useState, useEffect, useRef } from "react";
import { useLocation } from "wouter";
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Sun,
  CheckSquare,
  Inbox,
  Folder,
  Users,
  MessageSquare,
  BookOpen,
  ScrollText,
  Sparkles,
  Settings,
  Mail,
  Plus,
  Search,
  FileText,
  StickyNote,
} from "lucide-react";
import {
  useListProjects,
  getListProjectsQueryKey,
} from "@workspace/api-client-react";

const API_BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

interface SearchTask {
  id: string;
  title: string;
  projectName?: string;
  status?: string;
}

interface SearchContact {
  id: string;
  name: string;
  email?: string | null;
}

interface SearchDocument {
  id: string;
  filename: string;
  contentType?: string | null;
}

interface SearchMail {
  id: string;
  subject?: string | null;
  fromAddress?: string | null;
  accountId: string;
}

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onOpenCapture?: () => void;
  onOpenShortcuts?: () => void;
}

const NAV_ITEMS = [
  { label: "Aujourd'hui", href: "/app/today", icon: Sun, shortcut: "T" },
  { label: "Actions", href: "/app/actions", icon: CheckSquare },
  { label: "Inbox", href: "/app/inbox", icon: Inbox, shortcut: "I" },
  { label: "Projets", href: "/app/projects", icon: Folder, shortcut: "P" },
  { label: "CRM", href: "/app/crm", icon: Users },
  { label: "Chat IA", href: "/app/chat", icon: MessageSquare },
  { label: "Bibliothèque", href: "/app/library", icon: BookOpen },
  { label: "Décisions", href: "/app/decisions", icon: ScrollText },
  { label: "Mails", href: "/app/mail", icon: Mail },
  { label: "Réglages", href: "/app/settings", icon: Settings },
];

export default function CommandPalette({
  open,
  onOpenChange,
  onOpenCapture,
  onOpenShortcuts,
}: CommandPaletteProps) {
  const [, navigate] = useLocation();
  const [query, setQuery] = useState("");
  const [tasks, setTasks] = useState<SearchTask[]>([]);
  const [contacts, setContacts] = useState<SearchContact[]>([]);
  const [documents, setDocuments] = useState<SearchDocument[]>([]);
  const [mails, setMails] = useState<SearchMail[]>([]);
  const [searching, setSearching] = useState(false);
  const [creatingTask, setCreatingTask] = useState(false);
  const [taskCreating, setTaskCreating] = useState(false);

  const { data: projects = [] } = useListProjects(
    { status: "active" },
    {
      query: {
        queryKey: getListProjectsQueryKey({ status: "active" }),
        staleTime: 60_000,
        enabled: open,
      },
    },
  );

  // Reset when the dialog closes
  useEffect(() => {
    if (!open) {
      setQuery("");
      setTasks([]);
      setContacts([]);
      setDocuments([]);
      setMails([]);
      setCreatingTask(false);
      setTaskCreating(false);
    }
  }, [open]);

  const handleCreateTask = async () => {
    const title = query.trim();
    if (!title || taskCreating) return;
    setTaskCreating(true);
    try {
      await fetch(`${API_BASE}/api/tasks`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title }),
      });
    } catch {
      // best-effort
    } finally {
      onOpenChange(false);
      setCreatingTask(false);
      setTaskCreating(false);
      setQuery("");
    }
  };

  useEffect(() => {
    if (query.length < 2) {
      setTasks([]);
      setContacts([]);
      setDocuments([]);
      setMails([]);
      return;
    }
    const controller = new AbortController();
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const enc = encodeURIComponent(query);
        const [tRes, cRes, dRes, mRes] = await Promise.all([
          fetch(`${API_BASE}/api/tasks?q=${enc}&limit=5`, {
            credentials: "include",
            signal: controller.signal,
          }),
          fetch(`${API_BASE}/api/crm/contacts?q=${enc}&limit=5`, {
            credentials: "include",
            signal: controller.signal,
          }),
          fetch(`${API_BASE}/api/library/documents?q=${enc}&limit=5`, {
            credentials: "include",
            signal: controller.signal,
          }),
          fetch(`${API_BASE}/api/mail/search?q=${enc}&limit=5`, {
            credentials: "include",
            signal: controller.signal,
          }),
        ]);
        if (tRes.ok) {
          const data = (await tRes.json()) as SearchTask[];
          setTasks(Array.isArray(data) ? data.slice(0, 5) : []);
        }
        if (cRes.ok) {
          const data = (await cRes.json()) as SearchContact[];
          setContacts(Array.isArray(data) ? data.slice(0, 5) : []);
        }
        if (dRes.ok) {
          const data = (await dRes.json()) as { documents: SearchDocument[] };
          setDocuments(Array.isArray(data?.documents) ? data.documents.slice(0, 5) : []);
        }
        if (mRes.ok) {
          const data = (await mRes.json()) as { messages: SearchMail[] };
          setMails(Array.isArray(data?.messages) ? data.messages.slice(0, 5) : []);
        }
      } catch {
        // ignore abort
      } finally {
        setSearching(false);
      }
    }, 220);
    return () => {
      clearTimeout(t);
      controller.abort();
    };
  }, [query]);

  const run = (fn: () => void) => {
    onOpenChange(false);
    setTimeout(fn, 80);
  };

  const filteredNav = query
    ? NAV_ITEMS.filter((n) =>
        n.label.toLowerCase().includes(query.toLowerCase()),
      )
    : NAV_ITEMS;

  const filteredProjects = query
    ? projects
        .filter((p) => p.name.toLowerCase().includes(query.toLowerCase()))
        .slice(0, 5)
    : projects.slice(0, 4);

  const hasSearchResults =
    tasks.length > 0 || contacts.length > 0 || documents.length > 0 || mails.length > 0;

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput
        placeholder={
          creatingTask
            ? "Titre de la tâche… (Entrée pour créer, Échap pour annuler)"
            : "Rechercher, naviguer, capturer…"
        }
        value={query}
        onValueChange={setQuery}
        onKeyDown={(e) => {
          if (creatingTask && e.key === "Enter") {
            e.preventDefault();
            void handleCreateTask();
          }
          if (creatingTask && e.key === "Escape") {
            e.preventDefault();
            setCreatingTask(false);
            setQuery("");
          }
        }}
      />
      {/* Task creation mode: shows a hint instead of the command list */}
      {creatingTask ? (
        <div className="py-8 text-center">
          <CheckSquare className="w-8 h-8 mx-auto mb-3 text-primary opacity-60" />
          {query.trim() ? (
            <p className="text-sm font-medium">
              {taskCreating ? "Création en cours…" : `Appuyez sur Entrée pour créer`}
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              Saisissez le titre de la tâche
            </p>
          )}
          {query.trim() && !taskCreating && (
            <p className="text-xs text-muted-foreground mt-1 max-w-[280px] mx-auto truncate">
              « {query.trim()} »
            </p>
          )}
          <p className="text-[11px] text-muted-foreground mt-3">
            Échap pour annuler
          </p>
        </div>
      ) : (
      <CommandList className="max-h-[420px]">
        <CommandEmpty>
          {searching ? (
            <span className="text-muted-foreground text-sm">
              Recherche en cours…
            </span>
          ) : (
            <span>Aucun résultat pour « {query} »</span>
          )}
        </CommandEmpty>

        {/* Quick actions */}
        {!query && (
          <CommandGroup heading="Actions rapides">
            <CommandItem
              onSelect={() => run(() => onOpenCapture?.())}
              value="capture nouvelle capture texte voix note"
            >
              <Sparkles className="w-4 h-4" />
              Nouvelle capture
              <CommandShortcut>N</CommandShortcut>
            </CommandItem>
            <CommandItem
              onSelect={() => {
                setQuery("");
                setCreatingTask(true);
              }}
              value="nouvelle tâche créer tache ajouter"
            >
              <CheckSquare className="w-4 h-4" />
              Nouvelle tâche
            </CommandItem>
            <CommandItem
              onSelect={() =>
                run(() => {
                  onOpenCapture?.();
                })
              }
              value="nouvelle note mémo créer note"
            >
              <StickyNote className="w-4 h-4" />
              Nouvelle note
            </CommandItem>
            <CommandItem
              onSelect={() => run(() => navigate("/app/inbox"))}
              value="inbox aller inbox"
            >
              <Inbox className="w-4 h-4" />
              Ouvrir l'Inbox
              <CommandShortcut>I</CommandShortcut>
            </CommandItem>
            <CommandItem
              onSelect={() => run(() => onOpenShortcuts?.())}
              value="raccourcis clavier aide shortcuts"
            >
              <Search className="w-4 h-4" />
              Raccourcis clavier
              <CommandShortcut>?</CommandShortcut>
            </CommandItem>
          </CommandGroup>
        )}

        {/* Navigation */}
        {filteredNav.length > 0 && (
          <>
            {!query && <CommandSeparator />}
            <CommandGroup heading="Navigation">
              {filteredNav.map((item) => (
                <CommandItem
                  key={item.href}
                  onSelect={() => run(() => navigate(item.href))}
                  value={`nav ${item.label} ${item.href}`}
                >
                  <item.icon className="w-4 h-4" />
                  {item.label}
                  {item.shortcut && (
                    <CommandShortcut>{item.shortcut}</CommandShortcut>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {/* Projects */}
        {filteredProjects.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Projets actifs">
              {filteredProjects.map((p) => (
                <CommandItem
                  key={p.id}
                  onSelect={() => run(() => navigate(`/app/projects/${p.id}`))}
                  value={`projet ${p.name}`}
                >
                  <Folder className="w-4 h-4" />
                  {p.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {/* Search results */}
        {hasSearchResults && (
          <>
            <CommandSeparator />
            {tasks.length > 0 && (
              <CommandGroup heading="Tâches">
                {tasks.map((t) => (
                  <CommandItem
                    key={t.id}
                    onSelect={() => run(() => navigate("/app/actions"))}
                    value={`tache ${t.title}`}
                  >
                    <CheckSquare className="w-4 h-4" />
                    <span className="truncate flex-1">{t.title}</span>
                    {t.projectName && (
                      <span className="text-xs text-muted-foreground ml-2 shrink-0">
                        {t.projectName}
                      </span>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {contacts.length > 0 && (
              <CommandGroup heading="Contacts">
                {contacts.map((c) => (
                  <CommandItem
                    key={c.id}
                    onSelect={() => run(() => navigate(`/app/crm/${c.id}`))}
                    value={`contact ${c.name} ${c.email ?? ""}`}
                  >
                    <Users className="w-4 h-4" />
                    <span className="truncate flex-1">{c.name}</span>
                    {c.email && (
                      <span className="text-xs text-muted-foreground ml-2 shrink-0 truncate max-w-[160px]">
                        {c.email}
                      </span>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {documents.length > 0 && (
              <CommandGroup heading="Documents">
                {documents.map((d) => (
                  <CommandItem
                    key={d.id}
                    onSelect={() => run(() => navigate("/app/library"))}
                    value={`document ${d.filename}`}
                  >
                    <FileText className="w-4 h-4" />
                    <span className="truncate flex-1">{d.filename}</span>
                    {d.contentType && (
                      <span className="text-xs text-muted-foreground ml-2 shrink-0">
                        {d.contentType.split("/")[1] ?? d.contentType}
                      </span>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {mails.length > 0 && (
              <CommandGroup heading="Mails">
                {mails.map((m) => (
                  <CommandItem
                    key={m.id}
                    onSelect={() => run(() => navigate("/app/mail"))}
                    value={`mail ${m.subject ?? ""} ${m.fromAddress ?? ""}`}
                  >
                    <Mail className="w-4 h-4" />
                    <span className="truncate flex-1">
                      {m.subject ?? "(Sans objet)"}
                    </span>
                    {m.fromAddress && (
                      <span className="text-xs text-muted-foreground ml-2 shrink-0 truncate max-w-[160px]">
                        {m.fromAddress}
                      </span>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </>
        )}
      </CommandList>
      )}
    </CommandDialog>
  );
}
